import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "/logo.png";

const navLinks = [
  { label: 'Leistungen', href: '#leistungen' },
  { label: 'Über uns', href: '#ueber-uns' },
  { label: 'Historie', href: '#historie' },
  { label: 'Referenzen', href: '#referenzen' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Kontakt', href: '#kontakt' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white ${
        scrolled
          ? 'shadow-lg py-2'
          : 'shadow-sm py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <a href="#hero" onClick={() => scrollTo('#hero')} className="flex items-center gap-3" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="Daniels GmbH Logo" className="h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover" />
          <div className="hidden sm:block leading-tight">
            <span className="font-['Outfit'] font-bold text-base tracking-tight text-[#1A3A22] block">
              K.H. und B. Daniels
            </span>
            <span className="block text-xs tracking-[0.12em] uppercase text-[#2D5A27]/70">
              Elektroinstallationen GmbH
            </span>
          </div>
        </a>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              data-testid={`nav-link-${link.href.replace('#', '')}`}
              className={`font-['Manrope'] text-sm font-medium tracking-wide transition-colors duration-300 hover:text-[#10B981] text-[#1A3A22]`}
            >
              {link.label}
            </button>
          ))}
          <a
            href="tel:+492282346 44"
            data-testid="nav-call-button"
            className="flex items-center gap-2 bg-[#2D5A27] hover:bg-[#10B981] text-white px-6 py-2.5 font-semibold text-sm tracking-wider uppercase transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
          >
            <Phone className="w-4 h-4" strokeWidth={2} />
            Jetzt anrufen
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-2 text-[#1A3A22]`}
        >
          {mobileOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100 shadow-2xl"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  data-testid={`mobile-nav-${link.href.replace('#', '')}`}
                  className="block w-full text-left py-3 text-[#1A3A22] font-['Manrope'] font-medium text-base border-b border-gray-100 hover:text-[#10B981] transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <a
                href="tel:+492282346 44"
                data-testid="mobile-call-button"
                className="flex items-center justify-center gap-2 mt-4 bg-[#2D5A27] text-white py-3 font-semibold text-sm tracking-wider uppercase w-full"
              >
                <Phone className="w-4 h-4" />
                Jetzt Anrufen
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
